import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetSingleCampaignQuery,
  useGetCampaignVersionHistoryQuery,
  useGetVersionUsersQuery,
  useGetCampaignSuppressionListsQuery,
} from "../../RTKQuery/CampaignService/campaignApi";
import DataTable from "../UIKit/CollapsibleTable";
import Loader from "../Loader/Loader";
import PageComponentHeader from "../PageComponentHeader";
import CampaignStatusChip from "../UIKit/Chip/CampaignStatusChip";
import VersionChip from "../UIKit/Chip/VersionChip";
import SuppressionListsModal from "../Modals/SuppressionListsModal/SuppressionListsModal";
import { Box, Typography, Paper, Tabs, Tab, Chip, Button } from "@mui/material";
import {
  containerBox,
  overviewPaper,
  headerSection,
  titleSection,
  titleTypography,
  sourceChip,
  statusSection,
} from "../CampaignDetails/styles";
import {
  sourceColors,
  defaultSourceColors,
  sourceChipBase,
} from "../CampaignsList/styles";
import { versionDetailsStyles } from "./styles";

const versionHistoryColumns = [
  {
    accessorKey: "email",
    header: "Email",
    enableSorting: false,
    size: 200,
    Cell: ({ cell }) => {
      const value = cell?.getValue();
      return value === "" || value === null || value === undefined
        ? "N/A"
        : value;
    },
  },
  {
    accessorKey: "firstName",
    header: "First Name",
    enableSorting: false,
    size: 150,
    Cell: ({ cell }) => {
      const value = cell?.getValue();
      return value === "" || value === null || value === undefined
        ? "N/A"
        : value;
    },
  },
  {
    accessorKey: "lastName",
    header: "Last Name",
    enableSorting: false,
    size: 150,
    Cell: ({ cell }) => {
      const value = cell?.getValue();
      return value === "" || value === null || value === undefined
        ? "N/A"
        : value;
    },
  },
  {
    accessorKey: "emailName",
    header: "Generic Name",
    enableSorting: false,
    size: 180,
    Cell: ({ cell }) => {
      const value = cell?.getValue();
      return value === "" || value === null || value === undefined
        ? "N/A"
        : value;
    },
  },
  {
    accessorKey: "emailCompany",
    header: "Generic Company Name",
    enableSorting: false,
    size: 200,
    Cell: ({ cell }) => {
      const value = cell?.getValue();
      return value === "" || value === null || value === undefined
        ? "N/A"
        : value;
    },
  },
];

const VersionHistoryDetails = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([]);
  const [loader, setLoader] = useState(true);
  const [suppressionModalOpen, setSuppressionModalOpen] = useState(false);

  const { data: campaignData, isLoading: isLoadingCampaign } =
    useGetSingleCampaignQuery(campaignId, {
      skip: !campaignId,
    });

  const { data: versionHistory, isLoading: isLoadingVersions } =
    useGetCampaignVersionHistoryQuery(campaignId, {
      skip: !campaignId,
    });

  const versions = Array.isArray(versionHistory)
    ? versionHistory
    : versionHistory?.versions || [];
  const suppressionVersions = versions.filter((v) => {
    const type = v.versionType?.toLowerCase();
    return (
      type === "suppress" || type === "suppression" || type === "suppressed"
    );
  });
  const filterVersions = versions.filter((v) => {
    const type = v.versionType?.toLowerCase();
    return type === "filter" || type === "filtered";
  });
  const exportVersions = versions.filter((v) => {
    const type = v.versionType?.toLowerCase();
    return type === "export" || type === "exported";
  });

  const allTabs = [
    ...filterVersions.map((v) => ({ type: "filter", version: v })),
    ...suppressionVersions.map((v) => ({ type: "suppress", version: v })),
    ...exportVersions.map((v) => ({ type: "export", version: v })),
  ].sort((a, b) => {
    const dateA = new Date(a.version.createdAt);
    const dateB = new Date(b.version.createdAt);
    return dateA - dateB;
  });

  const selectedVersion = allTabs[selectedTab]?.version;
  const selectedVersionType = allTabs[selectedTab]?.type;

  const { data: versionUsersData, isLoading: isLoadingVersionUsers } =
    useGetVersionUsersQuery(
      {
        campaignId,
        versionId: selectedVersion?.id,
        page: pagination.pageIndex,
        rows: pagination.pageSize,
      },
      {
        skip: !selectedVersion,
      }
    );

  const { data: suppressionListsData, isLoading: isLoadingSuppressionLists } =
    useGetCampaignSuppressionListsQuery(campaignId, {
      skip: !campaignId || !suppressionModalOpen,
    });

  const usersData = versionUsersData;
  const isLoadingUsers = isLoadingVersionUsers;
  const isFetchingUsers =
    selectedVersionType === "suppress"
      ? false // RTK Query doesn't expose isFetching for suppression query
      : false; // RTK Query doesn't expose isFetching for version query

  useEffect(() => {
    if (campaignData && versionHistory) {
      setLoader(false);
    }
  }, [campaignData, versionHistory]);

  useEffect(() => {
    if (usersData && !isLoadingUsers) {
      setLoader(false);
    } else if (isLoadingUsers) {
      setLoader(true);
    }
  }, [usersData, isLoadingUsers, pagination]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const handleOpenSuppressionModal = () => {
    setSuppressionModalOpen(true);
  };

  const handleCloseSuppressionModal = () => {
    setSuppressionModalOpen(false);
  };

  const getVersionLabel = (version) => {
    const typeLabel =
      version.versionType?.charAt(0).toUpperCase() +
      version.versionType?.slice(1) || "";
    const versionNumber = version.versionNumber || "";
    return typeLabel && versionNumber
      ? `${typeLabel} list ${versionNumber}`
      : versionNumber || "N/A";
  };

  if (isLoadingCampaign || isLoadingVersions || loader) {
    return (
      <div className="noData">
        <Loader />
      </div>
    );
  }

  if (!campaignData) {
    return (
      <Box sx={versionDetailsStyles.notFoundBox}>
        <Typography variant="h5" sx={versionDetailsStyles.notFoundTitle}>
          Campaign Not Found
        </Typography>
        <Typography sx={versionDetailsStyles.notFoundBody}>
          The campaign you're looking for doesn't exist.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PageComponentHeader allowBack></PageComponentHeader>

      <Box sx={containerBox}>
        <Paper sx={versionDetailsStyles.compactPaper} elevation={0}>
          <Box sx={versionDetailsStyles.compactHeader}>
            <Box sx={versionDetailsStyles.titleRow}>
              <Typography variant="h5" sx={versionDetailsStyles.compactTitle}>
                {campaignData?.campaignTitle || "Campaign Title"}
              </Typography>
              <CampaignStatusChip status={campaignData?.status} size="medium" />
            </Box>
            <Box sx={versionDetailsStyles.infoRow}>
              {campaignData?.campaignSource && (
                <Chip
                  label={campaignData.campaignSource}
                  size="small"
                  sx={{
                    ...(sourceColors[campaignData.campaignSource] ||
                      defaultSourceColors),
                    ...sourceChipBase,
                  }}
                />
              )}
              {campaignData?.sentFromEmails &&
                Array.isArray(campaignData.sentFromEmails) &&
                campaignData.sentFromEmails.length > 0 && (
                  <Box sx={versionDetailsStyles.infoItem}>
                    <Typography sx={versionDetailsStyles.infoLabel}>
                      Sent From:
                    </Typography>
                    <Box sx={versionDetailsStyles.senderEmailsList}>
                      {campaignData.sentFromEmails.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          size="small"
                          sx={versionDetailsStyles.senderEmailChip}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
            </Box>
            {campaignData?.campaignSubject && (
              <Box sx={versionDetailsStyles.subjectRow}>
                <Typography sx={versionDetailsStyles.infoLabel}>
                  Subject:
                </Typography>
                <Typography sx={versionDetailsStyles.infoValue}>
                  {campaignData.campaignSubject}
                </Typography>
              </Box>
            )}
            <Box sx={versionDetailsStyles.buttonRow}>
              <Button
                variant="outlined"
                onClick={handleOpenSuppressionModal}
                sx={versionDetailsStyles.viewSuppressionButton}
              >
                View Suppression Lists
              </Button>
            </Box>
          </Box>
        </Paper>

        {allTabs.length > 0 ? (
          <>
            <Box sx={versionDetailsStyles.tabsContainer}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={versionDetailsStyles.tabs}
              >
                {allTabs.map((tab, index) => (
                  <Tab
                    key={`${tab.type}-${tab.version.id}`}
                    label={
                      <VersionChip
                        label={getVersionLabel(tab.version)}
                        variant="type"
                        type={tab.version.versionType}
                      />
                    }
                    sx={versionDetailsStyles.tab}
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={versionDetailsStyles.tableContainer}>
              {isLoadingUsers || loader ? (
                <Box sx={versionDetailsStyles.loaderBox}>
                  <Loader />
                </Box>
              ) : usersData?.users && usersData.users.length > 0 ? (
                <DataTable
                  columns={versionHistoryColumns}
                  data={usersData.users || []}
                  totalData={usersData.total || 0}
                  sorting={sorting}
                  setSorting={setSorting}
                  pagination={pagination}
                  setPagination={setPagination}
                  setLoader={setLoader}
                  enableCollapsibleRow={false}
                  manualPagination={true}
                />
              ) : (
                <Paper sx={versionDetailsStyles.emptyStatePaper}>
                  <Typography sx={versionDetailsStyles.emptyStateText}>
                    No users found for this version.
                  </Typography>
                </Paper>
              )}
            </Box>
          </>
        ) : (
          <Paper sx={versionDetailsStyles.emptyStatePaper}>
            <Typography sx={versionDetailsStyles.emptyStateText}>
              No version history found for this campaign.
            </Typography>
          </Paper>
        )}
      </Box>

      <SuppressionListsModal
        open={suppressionModalOpen}
        onClose={handleCloseSuppressionModal}
        data={suppressionListsData || []}
        isLoading={isLoadingSuppressionLists}
      />
    </>
  );
};

export default VersionHistoryDetails;
